import { z } from "zod";
import { KeyActivity, KeyPolicy, KeyStatus } from "./api-access.js";

/*
 * OAuth for MCP clients (docs/oauth.md): claude.ai, the Claude apps,
 * ChatGPT and other MCP clients sign in through our panel. The person sees
 * a consent screen, chooses projects and a policy like in the key wizard,
 * and the client becomes a "connected app" of theirs.
 */

/** How the client introduced itself: registered by itself (RFC 7591) or by a metadata document URL. */
export const OAuthClientKind = z.enum(["dynamic", "metadata_document"]);
export type OAuthClientKind = z.infer<typeof OAuthClientKind>;

export const OAuthClientView = z.object({
  /** Our id of a dynamic client, or the HTTPS URL of its metadata document. */
  id: z.string(),
  /** The name the client gave; self-asserted for dynamic clients. */
  name: z.string(),
  kind: OAuthClientKind,
  clientUri: z.string().nullable(),
});
export type OAuthClientView = z.infer<typeof OAuthClientView>;

/** A pending authorization request, as the consent screen shows it. */
export const OAuthConsent = z.object({
  id: z.string(),
  client: OAuthClientView,
  /** Where the browser returns after the answer. */
  redirectUri: z.string(),
  /** The redirect's host, shown prominently (MCP security considerations). */
  redirectHost: z.string(),
  /** The redirect goes to this computer (`localhost`, `127.0.0.1`): anyone can claim such a name. */
  localRedirect: z.boolean(),
  /** The protected resource the tokens are for (our MCP endpoint). */
  resource: z.string(),
  scope: z.string(),
  expiresAt: z.iso.datetime(),
  /** How long the app keeps access unless revoked (days). */
  grantDays: z.number().int().positive(),
  /** How long one access token lives (seconds); refresh tokens rotate. */
  accessTokenSeconds: z.number().int().positive(),
});
export type OAuthConsent = z.infer<typeof OAuthConsent>;

/** What the person allows: like a key's projects, policy and secret switch. */
export const ApproveOAuthRequest = z.object({
  projects: z.union([z.literal("all"), z.array(z.uuid()).min(1).max(500)]),
  policy: KeyPolicy,
  allowSecret: z.boolean().default(false),
});
export type ApproveOAuthRequest = z.input<typeof ApproveOAuthRequest>;

/** Where the browser goes next: the client's redirect with a code, or with `error=access_denied`. */
export const OAuthRedirect = z.object({ redirectTo: z.string() });
export type OAuthRedirect = z.infer<typeof OAuthRedirect>;

/** A connected app: an OAuth client with what a person allowed it. */
export const ConnectedApp = z.object({
  id: z.uuid(),
  /** The client's name as the person saw it on the consent screen. */
  name: z.string(),
  client: OAuthClientView,
  redirectHost: z.string(),
  user: z.object({ id: z.uuid(), name: z.string() }),
  projects: z.union([z.literal("all"), z.array(z.object({ id: z.uuid(), name: z.string() }))]),
  policy: KeyPolicy,
  policyName: z.string(),
  allowSecret: z.boolean(),
  status: KeyStatus,
  expiresAt: z.iso.datetime(),
  revokedAt: z.iso.datetime().nullable(),
  lastUsed: z
    .object({ at: z.iso.datetime(), ip: z.string().nullable(), client: z.string().nullable() })
    .nullable(),
  requestCount: z.number().int().nonnegative(),
  createdAt: z.iso.datetime(),
});
export type ConnectedApp = z.infer<typeof ConnectedApp>;

/** One audit entry of a connected app: a call it made, or a change made to it. */
export const ConnectedAppActivity = KeyActivity.omit({ key: true });
export type ConnectedAppActivity = z.infer<typeof ConnectedAppActivity>;
